import React, { useEffect, useState } from "react";
import { PageContainer, PageHeader, Card, ConfirmDelete } from "./Shared";
import { adminApi } from "./api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import ImageUpload from "./ImageUpload";

const DEFAULT_INDUSTRIES = ["Commercial", "Healthcare", "Industrial", "Hospitality", "Data Centre", "Renewables"];

const empty = {
  client: "", contractor: "", location: "", industry: "Commercial",
  summary: "", products: [], image_id: null, order: 0, status: "published",
};

/**
 * Client Projects Manager — CRUD for the /clients-portfolio bento grid.
 * Backed by `db.projects`, served by GET /api/client-projects.
 */
export default function ClientProjectsManager() {
  const [items, setItems] = useState([]);
  const [industries, setIndustries] = useState(DEFAULT_INDUSTRIES);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [productsRaw, setProductsRaw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [projRes, sc] = await Promise.all([
      adminApi.get("/admin/projects"),
      adminApi.get("/site-content"),
    ]);
    setItems(projRes.data);
    // Merge portfolio.industries (JSON list) with defaults + any tags already used on projects
    let cms = [];
    try {
      const raw = sc.data?.["portfolio.industries"];
      const parsed = raw ? JSON.parse(raw) : [];
      cms = (parsed || []).map((x) => (typeof x === "string" ? x : (x?.label || ""))).filter(Boolean);
    } catch { /* ignore parse errors */ }
    const used = (projRes.data || []).map((p) => (p.industry || "").trim()).filter(Boolean);
    const merged = Array.from(new Set([...cms, ...DEFAULT_INDUSTRIES, ...used]))
      .filter((v) => v.toLowerCase() !== "all");
    setIndustries(merged);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => {
    setEditing(null); setForm(empty); setProductsRaw(""); setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...empty, ...p });
    setProductsRaw((p.products || []).join(", "));
    setOpen(true);
  };

  /**
   * Ensure the chosen industry exists in `portfolio.industries` so it also
   * appears as a filter chip on the public page. Idempotent: only writes when
   * the tag is new. Keeps "All" first if the list already has it.
   */
  const persistIndustryIfNew = async (tag) => {
    const t = (tag || "").trim();
    if (!t || t.toLowerCase() === "all") return;
    let parsed = [];
    try {
      const sc = await adminApi.get("/site-content");
      const raw = sc.data?.["portfolio.industries"];
      parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) parsed = [];
    } catch { parsed = []; }
    const labels = parsed.map((x) => (typeof x === "string" ? x : (x?.label || ""))).filter(Boolean);
    if (labels.some((x) => x.toLowerCase() === t.toLowerCase())) return;
    const next = labels.length > 0 ? [...labels, t] : ["All", ...DEFAULT_INDUSTRIES, t];
    if (!next.some((x) => x.toLowerCase() === "all")) next.unshift("All");
    await adminApi.put("/admin/page-content", {
      key: "portfolio.industries",
      value: JSON.stringify(next),
    });
    setIndustries((prev) => Array.from(new Set([...prev, t])));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client) return toast.error("Client name required");
    const products = productsRaw.split(",").map((s) => s.trim()).filter(Boolean);
    const industry = (form.industry || "").trim();
    const payload = { ...form, products, industry };
    setBusy(true);
    try {
      if (editing) await adminApi.put(`/admin/projects/${editing.id}`, payload);
      else await adminApi.post("/admin/projects", payload);
      await persistIndustryIfNew(industry);
      toast.success(editing ? "Updated" : "Project added");
      setOpen(false);
      await load();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    await adminApi.delete(`/admin/projects/${id}`);
    await load();
    toast.success("Deleted");
  };

  return (
    <PageContainer>
      <PageHeader
        title="Clients Portfolio"
        subtitle="Projects shown on the public /clients-portfolio page. Filter tabs come from Page Content → portfolio.industries — new tags typed here are added automatically."
        action={<Button onClick={openCreate} className="text-white" style={{ background: "#1B6FE0" }} data-testid="project-new"><Plus size={14} weight="bold" /> Add project</Button>}
      />

      <Card>
        <ul className="divide-y divide-slate-100" data-testid="project-list">
          {items.length === 0 && <li className="p-8 text-center text-[#6B7A8D]">No projects yet.</li>}
          {items.map((p) => (
            <li key={p.id} className="p-4 flex items-center gap-3" data-testid={`project-item-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[14.5px] truncate" style={{ color: "#0F2A6E" }}>{p.client}</div>
                <div className="text-[12px] text-[#6B7A8D] truncate">
                  {p.industry || "—"} · {p.location || "—"} · {p.contractor || "no contractor"} · order {p.order} · {p.status}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`project-edit-${p.id}`}><PencilSimple size={13} weight="bold" /></Button>
              <ConfirmDelete title="Delete this project?" description={`&quot;${p.client}&quot; will be removed from the Clients Portfolio.`} onConfirm={() => del(p.id)} testId={`project-del-${p.id}`}>
                <Button variant="outline" size="sm" className="text-[#1B6FE0] hover:bg-blue-50 hover:text-[#1B6FE0]" data-testid={`project-del-${p.id}`}><Trash size={13} weight="bold" /></Button>
              </ConfirmDelete>
            </li>
          ))}
        </ul>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit project" : "Add project"}</DialogTitle>
            <DialogDescription>Projects populate the /clients-portfolio bento grid on the public site.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Client / Project name</Label>
              <Input className="mt-1.5" value={form.client} onChange={(e) => set("client", e.target.value)} required data-testid="project-client" placeholder="e.g. Enterprise Data Centre" />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Contractor</Label>
                <Input className="mt-1.5" value={form.contractor} onChange={(e) => set("contractor", e.target.value)} data-testid="project-contractor" placeholder="e.g. Alpha Data" />
              </div>
              <div>
                <Label>Location</Label>
                <Input className="mt-1.5" value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="project-location" placeholder="e.g. Dubai, UAE" />
              </div>
            </div>
            <div>
              <Label>Industry tag</Label>
              <Input
                className="mt-1.5"
                list="project-industry-options"
                value={form.industry}
                onChange={(e) => set("industry", e.target.value)}
                placeholder="Pick an existing tag or type a new one (e.g. Government, Retail…)"
                data-testid="project-industry"
              />
              <datalist id="project-industry-options">
                {industries.map((i) => <option key={i} value={i} />)}
              </datalist>
              <div className="mt-1 text-[11px] text-slate-400">
                New tags are added to the public filter tabs automatically. Existing tags: {industries.join(", ") || "—"}
              </div>
            </div>
            <div>
              <Label>Summary</Label>
              <Textarea rows={3} className="mt-1.5" value={form.summary} onChange={(e) => set("summary", e.target.value)} data-testid="project-summary" placeholder="1-2 sentence summary shown on the card…" />
            </div>
            <div>
              <Label>Products / capabilities (comma-separated · optional, hidden from public card)</Label>
              <Input className="mt-1.5" value={productsRaw} onChange={(e) => setProductsRaw(e.target.value)} data-testid="project-products" placeholder="UPS Systems, Structured Cabling, Access Control" />
            </div>
            <ImageUpload value={form.image_id} onChange={(id) => set("image_id", id)} label="Project image (optional — falls back to gradient tile)" testId="project-image" />
            <div className="flex items-center gap-3">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft (hidden)</SelectItem>
                </SelectContent>
              </Select>
              <Label className="ml-3">Order</Label>
              <Input type="number" className="w-24" value={form.order} onChange={(e) => set("order", parseInt(e.target.value || "0", 10))} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ background: "#1B6FE0" }} data-testid="project-save">
                {busy ? "Saving…" : (editing ? "Update project" : "Add project")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
